// apiService.js ATUALIZADO com suporte a busca de contas para PIX, CONCILIAÇÃO e IMPORTAÇÃO OFX

import axios from 'axios'
import toast from 'react-hot-toast'

// API Principal (Backend - porta 3001)
const api = axios.create({
  baseURL: '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// API WhatsApp Direct (porta 21465)
const whatsappApi = axios.create({
  baseURL: '',
  timeout: 90000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Interceptadores
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    console.log('📤 API Request:', config.method?.toUpperCase(), config.url)
    return config
  },
  (error) => Promise.reject(error)
)

api.interceptors.response.use(
  (response) => {
    console.log('📥 API Response:', response.status, response.config.url)
    return response
  },
  (error) => {
    console.error('❌ API Error:', error.message, error.config?.url)
    
    const message = error.response?.data?.message || error.message || 'Erro desconhecido'
    
    if (error.response?.status === 500) {
      toast.error('Erro interno do servidor')
    } else if (error.response?.status === 404) {
      toast.error('Recurso não encontrado')
    } else if (error.response?.status >= 400) {
      toast.error(message)
    } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      toast.error('Erro de conexão. Verifique se o backend está rodando.')
    }
    
    return Promise.reject(error)
  }
)

whatsappApi.interceptors.request.use(
  (config) => {
    console.log('📱 WhatsApp Request:', config.method?.toUpperCase(), config.url)
    return config
  },
  (error) => Promise.reject(error)
)

whatsappApi.interceptors.response.use(
  (response) => {
    console.log('📱 WhatsApp Response:', response.status, response.config.url)
    return response
  },
  (error) => {
    console.error('❌ WhatsApp Error:', error.message, error.config?.url)
    
    const message = error.response?.data?.message || error.message || 'Erro desconhecido'
    
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      toast.error('Erro de conexão com WhatsApp.')
    } else {
      toast.error(`WhatsApp: ${message}`)
    }
    
    return Promise.reject(error)
  }
)

// ==========================================
// SERVIÇOS
// ==========================================

export const apiService = {
  system: {
    async getHealth() {
      return await api.get('/api/health')
    },
    
    async startScheduler() {
      return await api.post('/api/system/start-scheduler')
    },
    
    async getInfo() {
      return await api.get('/api/system/info')
    }
  },

  paperless: {
    async getSsoUrl() {
      return await api.get('/api/paperless/sso')
    },

    async getRecentes(limit = 6) {
      return await api.get('/api/paperless/documentos/recentes', { params: { limit } })
    },

    async buscarDocumentos({ q, de, ate, tags, page = 1, pageSize = 12 } = {}) {
      return await api.get('/api/paperless/documentos', {
        params: {
          q: q || undefined,
          de: de || undefined,
          ate: ate || undefined,
          tags: tags?.length ? tags.join(',') : undefined,
          page,
          page_size: pageSize
        }
      })
    },

    async getTags() {
      return await api.get('/api/paperless/tags')
    },

    async compartilharDocumento(id, dias = 7) {
      return await api.post(`/api/paperless/documentos/${id}/compartilhar`, { dias })
    },

    async getThumbBlob(id) {
      return await api.get(`/api/paperless/documentos/${id}/thumb`, { responseType: 'blob' })
    },

    async getArquivoBlob(id) {
      return await api.get(`/api/paperless/documentos/${id}/arquivo`, { responseType: 'blob' })
    }
  },

  dashboard: {
    async getStats() {
      return await api.get('/api/dashboard/stats')
    },

    async getVendasMesVendedor(filial = 1) {
      return await api.get('/api/dashboard/vendas-mes-vendedor', { params: { filial } })
    }
  },

  inadimplencia: {
    async getIndice(filial = 1) {
      return await api.get('/api/inadimplencia/indice', { params: { filial } })
    }
  },

  clientes: {
    async buscar(termo = '', limit = 17, offset = 0) {
      return await api.get('/api/clientes', {
        params: { termo, limit, offset }
      })
    },

    async buscarPorOrdem(ordem) {
      return await api.get(`/api/clientes/${ordem}`)
    },

    /**
     * ⭐ MÉTODO ATUALIZADO: Buscar contas a receber
     * @param {number} ordem - Ordem do cliente
     * @param {number} filial - Filial (padrão: 1)
     * @param {boolean} paraPix - Se true, busca TODAS parcelas em aberto (não só em atraso)
     * 
     * EXEMPLOS:
     * - Página Cobranças: buscarContas(7441, 1, false) → apenas em atraso
     * - Página CobrançaPix: buscarContas(7441, 1, true) → todas em aberto
     */
    async buscarContas(ordem, filial = 1, paraPix = false) {
      console.log(`🔍 buscarContas: ordem=${ordem}, filial=${filial}, paraPix=${paraPix}`);
      
      return await api.get(`/api/clientes/${ordem}/contas`, {
        params: { 
          filial,
          para_pix: paraPix ? 'true' : 'false'
        }
      })
    },

    /**
     * 🆕 NOVO MÉTODO DEDICADO: Buscar contas para PIX
     * Busca TODAS as parcelas em aberto (incluindo não vencidas)
     * 
     * ALTERNATIVA simplificada ao uso de buscarContas(ordem, filial, true)
     */
    async buscarContasPix(ordem, filial = 1) {
      console.log(`💰 buscarContasPix: ordem=${ordem}, filial=${filial}`);
      
      return await api.get(`/api/clientes/${ordem}/contas-pix`, {
        params: { filial }
      })
    },

    /**
     * 🆕 NOVO MÉTODO: Buscar contas para conciliação PIX
     */
    async buscarContasConciliacao(ordem, filial = 1) {
      console.log(`🔗 buscarContasConciliacao: ordem=${ordem}, filial=${filial}`);
      
      return await api.get(`/api/clientes/${ordem}/contas-conciliacao`, {
        params: { filial }
      })
    },

    async buscarContasPorOrdens(ordens, filial = 1) {
      const ordensStr = Array.isArray(ordens) ? ordens.join(',') : String(ordens);
      return await api.get(`/api/clientes/contas-por-ordens`, {
        params: { ordens: ordensStr, filial }
      });
    },

    async obterResumoCobranca(ordem, filial = 1) {
      return await api.get(`/api/clientes/${ordem}/resumo-cobranca`, {
        params: { filial }
      })
    },

    async buscarComContas(termo = '', limit = 10, filial = 1) {
      return await api.get('/api/clientes/com-contas', {
        params: { termo, limit, filial }
      })
    }
  },

  produtos: {
    async buscar(termo = '', limit = 20, offset = 0) {
      return await api.get('/api/produtos/buscar', {
        params: { termo, limit, offset }
      })
    },

    async buscarPorOrdem(ordem) {
      return await api.get(`/api/produtos/${ordem}`)
    },

    async buscarPrecos(ordem, { todasTabelas = false, incluirCusto = false } = {}) {
      return await api.get(`/api/produtos/${ordem}/precos`, {
        params: { todasTabelas, incluirCusto }
      })
    }
  },

  cobranca: {
    async getClientesEmAtraso(diasMin = 30, diasMax = null, filial = 1) {
      const params = { diasMin, filial };
      if (diasMax !== null) {
        params.diasMax = diasMax;
      }
      
      return await api.get('/api/cobranca/clientes-em-atraso', { params })
    },

    async getLembretes(data = null) {
      const params = {};
      if (data) {
        params.data = data;
      }
      return await api.get('/api/lembretes', { params })
    },

    async salvarDadosCobranca(dados) {
      return await api.post('/api/cobranca/salvar-dados', dados)
    },

    async getDadosCobranca(ordemCliente) {
      return await api.get(`/api/cobranca/dados/${ordemCliente}`)
    },

    async getAgendamentos(data = null) {
      const params = {};
      if (data) {
        params.data = data;
      }
      return await api.get('/api/cobranca/agendamentos', { params })
    },

    async getAgendaCobrancas(dataInicial, dataFinal) {
      return await api.get('/api/cobranca/agenda', {
        params: { dataInicial, dataFinal }
      })
    },

    async registrarEnvioWhatsApp(dados) {
      return await api.post('/api/cobranca/registrar-envio-whatsapp', dados)
    },

    async getHistoricoWhatsApp(ordemCliente, limit = 10) {
      return await api.get(`/api/cobranca/historico-whatsapp/${ordemCliente}`, {
        params: { limit }
      })
    },

    async gerarListaImpressao(diasAtraso = 90, filial = 1, limit = 10, offset = 0) {
      return await api.get('/api/cobranca/lista-impressao', {
        params: { diasAtraso, filial, limit, offset }
      })
    },

    async getEstatisticas(filial = 1) {
      return await api.get('/api/cobranca/estatisticas', {
        params: { filial }
      })
    },

    async getConfiguracoes() {
      return await api.get('/api/cobranca/configuracoes')
    },

    async atualizarConfiguracao(chave, valor) {
      return await api.put('/api/cobranca/configuracoes', {
        chave,
        valor
      })
    },

    async testar() {
      return await api.get('/api/cobranca/test')
    },

    async executarAutomatica() {
      return await api.post('/api/cobranca/automatica')
    },

    async enviarManual(ordemCliente, mensagem) {
      return await api.post('/api/cobranca/manual', {
        ordemCliente,
        mensagem
      })
    }
  },

  pix: {
    async criar(dados) {
      return await api.post('/api/pix/criar', dados)
    },

    async buscar(ordemCliente, limit = 10) {
      return await api.get('/api/pix/links', {
        params: { ordemCliente, limit }
      })
    },

    async getStatus(pedidoId) {
      return await api.get(`/api/pix/status/${pedidoId}`)
    },

    async cancelar(pedidoId) {
      return await api.post(`/api/pix/cancelar/${pedidoId}`)
    },

    async getHistorico(params = {}) {
      return await api.get('/api/pix/historico', { params })
    },

    async getLocalImage(linkId) {
      return await api.get(`/api/pix/local-image/${linkId}`, {
        responseType: 'blob'
      })
    }
  },

  email: {
    enviar: async (dados) => {
      console.log('📧 apiService.email.enviar - Dados:', dados);
      
      if (!dados.descricao) {
        throw new Error('Campo descricao é obrigatório');
      }
      
      const response = await api.post('/api/email/enviar-link-pix', dados);
      return response.data;
    },

    testar: async (emailDestino) => {
      const response = await api.post('/api/email/testar', { email: emailDestino });
      return response.data;
    }
  },

  vendas: {
    async buscarPorSequencia(sequencia, filial = 1) {
      console.log(`🔍 buscarPorSequencia: ${sequencia}, filial: ${filial}`);
      const response = await api.get(`/api/vendas/sequencia/${sequencia}`, {
        params: { filial }
      });
      return response;
    },

    async buscarPorOrdem(ordem, filial = 1) {
      const response = await api.get(`/api/vendas/ordem/${ordem}`, {
        params: { filial }
      });
      return response.data;
    },

    async buscarItens(sequencia, filial = 1) {
      const response = await api.get(`/api/vendas/${sequencia}/itens`, {
        params: { filial }
      });
      return response.data;
    },

    async buscarCompleta(sequencia, filial = 1) {
      const response = await api.get(`/api/vendas/${sequencia}/completa`, {
        params: { filial }
      });
      return response.data;
    },

    async verificarPix(sequencia, filial = 1) {
      const response = await api.get(`/api/vendas/${sequencia}/verificar-pix`, {
        params: { filial }
      });
      return response.data;
    }
  },

  // ⭐ Serviços de Conciliação PIX
  baixaPix: {
    listar: () => api.get('/api/baixa-pix/fila'),
  },

  conciliacao: {
    /**
     * Buscar resumo de conciliação
     */
    async buscarResumo() {
      return await api.get('/api/conciliacao/resumo');
    },

    /**
     * Buscar links para conciliação
     */
    async buscarLinks(filtros = {}) {
      return await api.get('/api/conciliacao/links', {
        params: filtros
      });
    },

    /**
     * Atualizar data de entrada na conta (pix_bancario)
     */
    async atualizarDataPagamento(linkId, dataPagamento) {
      return await api.patch(`/api/conciliacao/link/${linkId}/data-pagamento`, {
        data_pagamento: dataPagamento
      });
    },

    /**
     * Salvar observações
     */
    async salvarObservacoes(linkId, observacoes) {
      return await api.post('/api/conciliacao/salvar-observacoes', {
        link_pix_id: linkId,
        observacoes
      });
    },

    /**
     * Conciliar pagamento
     */
    async conciliar(linkId, observacoes) {
      return await api.post('/api/conciliacao/conciliar', {
        link_pix_id: linkId,
        observacoes
      });
    },

    /**
     * ⭐ Vincular contas a receber manualmente
     * @param {number} linkPixId - ID do link PIX
     * @param {Array<number>} contasReceber - Array com Ordem das contas a receber
     * @param {string} observacoes - Observações sobre a vinculação
     */
    async vincularContas(linkId, contasReceber, observacoes, valorJuros = 0, parcelasDetalhes = []) {
      return await api.post('/api/conciliacao/vincular', {
        link_pix_id: linkId,
        parcelas: parcelasDetalhes,
        valor_juros: valorJuros,
        observacoes,
        usuario: 'operador'
      });
    },
    async registrarPixBancario(dados) {
      return await api.post('/api/conciliacao/registrar-pix-bancario', dados);
    },

    async confirmarDeposito(linkId) {
      return await api.patch(`/api/conciliacao/link/${linkId}/confirmar-deposito`, { usuario: 'operador' });
    },
  },

  // ⭐⭐⭐ NOVO: Serviço de Importação OFX ⭐⭐⭐
  importacaoOFX: {
    /**
     * Upload e processamento de arquivo OFX
     * @param {FormData} formData - FormData contendo o arquivo
     * @returns {Promise} Resultado do processamento
     */
    async upload(formData) {
      console.log('📤 Enviando arquivo OFX para processamento...');
      
      return await api.post('/api/importacao-ofx/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000, // 60 segundos para upload e processamento
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`📊 Upload progress: ${percentCompleted}%`);
        }
      });
    },

    /**
     * Listar importações realizadas
     * @param {Object} filtros - Filtros opcionais
     * @param {boolean} filtros.processado - Filtrar por status de processamento
     * @param {string} filtros.data_inicio - Data inicial (YYYY-MM-DD)
     * @param {string} filtros.data_fim - Data final (YYYY-MM-DD)
     * @param {number} filtros.limit - Limite de resultados
     * @returns {Promise} Lista de importações
     */
    async listar(filtros = {}) {
      console.log('📋 Buscando importações OFX:', filtros);
      
      return await api.get('/api/importacao-ofx/importacoes', {
        params: filtros
      });
    },

    /**
     * Buscar estatísticas de importações
     * @returns {Promise} Estatísticas agregadas
     */
    async estatisticas() {
      console.log('📊 Buscando estatísticas de importações...');
      
      return await api.get('/api/importacao-ofx/estatisticas');
    },

    /**
     * Marcar importação como processada
     * @param {number} importacaoId - ID da importação
     * @returns {Promise} Confirmação
     */
    async marcarProcessada(importacaoId) {
      console.log(`✅ Marcando importação ${importacaoId} como processada...`);
      
      return await api.put(`/api/importacao-ofx/${importacaoId}/processar`);
    },

    /**
     * Buscar detalhes de uma importação específica
     * @param {number} importacaoId - ID da importação
     * @returns {Promise} Detalhes da importação
     */
    async buscarPorId(importacaoId) {
      console.log(`🔍 Buscando importação ${importacaoId}...`);
      
      return await api.get(`/api/importacao-ofx/importacoes/${importacaoId}`);
    }
  },

  // ─── Análise de Crédito ───────────────────────────────────────────────────
  analiseCredito: {
    async buscarHistorico(ordemCliente) {
      return await api.get(`/api/analise-credito/historico/${ordemCliente}`)
    },

    async buscarPorCliente(ordemCliente) {
      return await api.get(`/api/analise-credito/cliente/${ordemCliente}`)
    },

    async salvar(dados) {
      return await api.post('/api/analise-credito', dados)
    },
    
    async uploadDocs(formData) {
      return await api.post('/api/analise-credito/upload-docs', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
    },

    async getDoc(arquivo) {
      return await api.get('/api/analise-credito/doc', {
        params: { arquivo },
        responseType: 'blob',
      });
    },

    async listarImagensCpf(cpf) {
      return await api.get(`/api/analise-credito/imagens-cpf/${cpf}`);
    },

    async getFoto(arquivo) {
      return await api.get('/api/analise-credito/foto', {
        params: { arquivo },
        responseType: 'blob',
      });
    },

    async enviarFoto(id, slot, blob) {
      const fd = new FormData();
      fd.append('slot', slot);
      fd.append('foto', blob, `${slot}.jpg`);
      return await api.post(`/api/analise-credito/${id}/fotos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
    },

    async removerFoto(id, slot) {
      return await api.delete(`/api/analise-credito/${id}/fotos/${slot}`);
    },
    async listarTodas(params = {}) {
      return await api.get('/api/analise-credito', { params });
    },

    async buscarPorId(id) {
      return await api.get(`/api/analise-credito/${id}`);
    },

    async marcarImpresso(id) {
      return await api.patch(`/api/analise-credito/${id}/marcar-impresso`);
    },

    async atualizarConfirmacaoZap(id, telefone, resposta) {
    return await api.patch(`/api/analise-credito/${id}/confirmacao-zap`, {
      telefone,
      resposta,
    });
    },

    async forcarLiberacao(id, motivo, usuario) {
      return await api.patch(`/api/analise-credito/${id}/forcar-liberacao`, {
        motivo,
        usuario,
      });
    },
  },

  // ─── Log de Erros (preço zerado / margem insuficiente) ───────────────────
  logsErros: {
    async listar(params = {}) {
      return await api.get('/api/logs-erros', { params });
    },
  },

  // ─── Margens Lucro/Juros (log de viabilidade das vendas — só admin) ──────
  margensLucroJuros: {
    async listar(params = {}) {
      return await api.get('/api/margens-lucro-juros', { params });
    },
  },

  // ─── Config de Markup Mínimo (página Margens) ────────────────────────────
  configMarkup: {
    async listar() {
      return await api.get('/api/config-markup');
    },
    async atualizar(ordemTabela, dados) {
      return await api.put(`/api/config-markup/${ordemTabela}`, dados);
    },
  },

  correcaoDescricao: {
    listar(params, signal) { return api.get('/api/produtos-correcao-descricao', { params, signal }); },
    marcar(id, dados) { return api.patch(`/api/produtos-correcao-descricao/${id}`, dados); },
  },

  logsSincronizacao: {
    listar(tipo, params, signal) {
      return api.get(`/api/sincronizacao/logs/${tipo}`, { params, signal });
    },
  },

  // ─── Estoque Mínimo/Ideal (POP-EST-001) ──────────────────────────────────
  estoqueMinimoIdeal: {
    async listar(filtros = {}) {
      return await api.get('/api/estoque-min-ideal', { params: filtros });
    },
    async status() {
      return await api.get('/api/estoque-min-ideal/status');
    },
    async detalhe(codigoProduto) {
      return await api.get(`/api/estoque-min-ideal/${codigoProduto}`);
    },
    async atualizar(codigoProduto, dados) {
      return await api.put(`/api/estoque-min-ideal/${codigoProduto}`, dados);
    },
    async recalcularTodos(periodoBuscaMeses = 6) {
      return await api.post('/api/estoque-min-ideal/recalcular', { periodoBuscaMeses });
    },
    async recalcularProduto(codigoProduto, periodoBuscaMeses = 6) {
      return await api.post(`/api/estoque-min-ideal/${codigoProduto}/recalcular`, { periodoBuscaMeses });
    },
    async obterParametros() {
      return await api.get('/api/estoque-min-ideal/config/parametros');
    },
    async atualizarParametros(dados) {
      return await api.put('/api/estoque-min-ideal/config/parametros', dados);
    },
    async exportarXlsx() {
      return await api.get('/api/estoque-min-ideal/exportar/xlsx', { responseType: 'blob' });
    },
    async enviarAlertas() {
      return await api.post('/api/estoque-min-ideal/enviar-alertas');
    },
  },

  // ─── Inventário Rotativo (POP-EST-001 — folhas de contagem cíclica) ─────
  inventarioRotativo: {
    async listar(filtros = {}) {
      return await api.get('/api/inventario-rotativo', { params: filtros });
    },
    async buscarProdutos(termo, params = {}) {
      return await api.get('/api/inventario-rotativo/produtos/buscar', { params: { termo, ...params } });
    },
    async previewCurvaAbc(params) {
      return await api.get('/api/inventario-rotativo/curva-abc/preview', { params });
    },
    async criar(dados) {
      return await api.post('/api/inventario-rotativo', dados);
    },
    async detalhe(id) {
      return await api.get(`/api/inventario-rotativo/${id}`);
    },
    async atualizarStatus(id, contado) {
      return await api.patch(`/api/inventario-rotativo/${id}/status`, { contado });
    },
  },

  // ─── Estoque de celulares (IMEI × ERP) ─────
  celularesEstoque: {
    async buscarProdutos(termo) {
      return await api.get('/api/celulares-estoque/produtos/buscar', { params: { termo } });
    },
    async listarModelos() {
      return await api.get('/api/celulares-estoque/modelos');
    },
    async incluirModelo(ordemProduto) {
      return await api.post('/api/celulares-estoque/modelos', { ordemProduto });
    },
    async removerModelo(ordemProduto) {
      return await api.delete(`/api/celulares-estoque/modelos/${ordemProduto}`);
    },
    async listarAparelhos(params = {}) {
      return await api.get('/api/celulares-estoque/aparelhos', { params });
    },
    async cadastrarAparelhos(dados) {
      return await api.post('/api/celulares-estoque/aparelhos', dados);
    },
    async baixarAparelho(id, dados) {
      return await api.put(`/api/celulares-estoque/aparelhos/${id}/baixa`, dados);
    },
    async estornarBaixa(id) {
      return await api.put(`/api/celulares-estoque/aparelhos/${id}/estorno`);
    },
    async listarSaidasPendentes() {
      return await api.get('/api/celulares-estoque/saidas-pendentes');
    },
    async concluirSaida(id, imei) {
      return await api.put(`/api/celulares-estoque/saidas-pendentes/${id}/concluir`, { imei });
    },
    async vendasMensais(meses) {
      return await api.get('/api/celulares-estoque/vendas-mensais', { params: { meses } });
    },
  },

  // ─── Caixa de Ideias dos colaboradores ─────
  pwa: {
    async listarAvisos(status = 'aberto') {
      return await api.get('/api/pwa/avisos', { params: { status } });
    },
    async resolverAviso(id) {
      return await api.put(`/api/pwa/avisos/${id}/resolver`);
    },
    async maisConsultados(dias = 30) {
      return await api.get('/api/pwa/mais-consultados', { params: { dias } });
    },
  },

  ideias: {
    async listar(filtros = {}) {
      return await api.get('/api/ideias', { params: filtros });
    },
    async pendentes() {
      return await api.get('/api/ideias/pendentes');
    },
    async criar(dados) {
      return await api.post('/api/ideias', dados);
    },
    async avaliar(id, dados) {
      return await api.patch(`/api/ideias/${id}/avaliacao`, dados);
    },
    async excluir(id) {
      return await api.delete(`/api/ideias/${id}`);
    },
  },

  // ─── Avaliação de Compra de Produto Novo (IA) — .clinerules/16-avaliacao-compra-ia.md ─────
  avaliacaoCompra: {
    async identificar(texto) {
      return await api.post('/api/avaliacao-compra/identificar', { texto });
    },
    async candidatos(termo) {
      return await api.get('/api/avaliacao-compra/candidatos', { params: { termo } });
    },
    async analogos(texto) {
      return await api.post('/api/avaliacao-compra/analogos', { texto });
    },
    async indicadores(codigo) {
      return await api.get(`/api/avaliacao-compra/indicadores/${codigo}`);
    },
    async pesquisaWeb(nomeProduto) {
      return await api.post('/api/avaliacao-compra/pesquisa-web', { nomeProduto });
    },
    async calcular(dados) {
      return await api.post('/api/avaliacao-compra/calcular', dados);
    },
    async salvar(dados) {
      return await api.post('/api/avaliacao-compra/salvar', dados);
    },
    async historico(filtros = {}) {
      return await api.get('/api/avaliacao-compra/historico', { params: filtros });
    },
  },

  // ─── Pedidos > Precificação — .clinerules/19-precificacao-pedidos.md ─────
  // Timeout maior: a análise/explicação da IA pode levar até ~2 min em notas grandes (2 tentativas).
  precificacao: {
    async importarNFe(arquivo) {
      const fd = new FormData();
      fd.append('arquivo', arquivo);
      return await api.post('/api/precificacao/importar-nfe', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 150000,
      });
    },
    async importarCTe(arquivo, { chaveNFe, cnpjLoja } = {}) {
      const fd = new FormData();
      fd.append('arquivo', arquivo);
      fd.append('chaveNFe', chaveNFe || '');
      fd.append('cnpjLoja', cnpjLoja || '');
      return await api.post('/api/precificacao/importar-cte', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    async calcularESalvar(dados) {
      return await api.post('/api/precificacao/calcular-e-salvar', dados, { timeout: 150000 });
    },
    async historico(filtros = {}) {
      return await api.get('/api/precificacao/historico', { params: filtros });
    },
    async buscar(id) {
      return await api.get(`/api/precificacao/${id}`);
    },
    // Vínculo produto da NF-e ↔ produto do ERP
    async produtosErp(termo) {
      return await api.get('/api/precificacao/produtos-erp', { params: { termo } });
    },
    async vinculos(filtros = {}) {
      return await api.get('/api/precificacao/vinculos', { params: filtros });
    },
    async vincular(dados) {
      return await api.post('/api/precificacao/vinculos', dados);
    },
    async desvincular(id) {
      return await api.delete(`/api/precificacao/vinculos/${id}`);
    },
    async solicitacoes(filtros = {}) {
      return await api.get('/api/precificacao/vinculos/solicitacoes', { params: filtros });
    },
    async solicitarLiberacao(dados) {
      return await api.post('/api/precificacao/vinculos/solicitacoes', dados);
    },
    async solicitarDesvinculo(vinculoId, { justificativa, codigoErpCorreto }) {
      return await api.post(`/api/precificacao/vinculos/${vinculoId}/solicitar-desvinculo`, { justificativa, codigoErpCorreto });
    },
    async decidirSolicitacao(id, { aprovar, motivo }) {
      return await api.post(`/api/precificacao/vinculos/solicitacoes/${id}/decidir`, { aprovar, motivo });
    },
  },

  // ─── Automação do Grupo de WhatsApp (página Whatsapp Automação) ─────────
  whatsappGrupo: {
    async listarAgendamentos() {
      return await api.get('/api/whatsapp-grupo/agendamentos');
    },
    async atualizarAgendamento(chave, dados) {
      return await api.put(`/api/whatsapp-grupo/agendamentos/${chave}`, dados);
    },
    async sugerirTexto(textoAtual) {
      return await api.post('/api/whatsapp-grupo/sugerir-texto', { textoAtual });
    },
  },

  // ─── Simulador de Viabilidade de Desconto ────────────────────────────────
  simulacaoViabilidade: {
    async salvar(dados) {
      return await api.post('/api/simulacao-viabilidade', dados);
    },
    async listarTodas(params = {}) {
      return await api.get('/api/simulacao-viabilidade', { params });
    },
    async buscarPorId(id) {
      return await api.get(`/api/simulacao-viabilidade/${id}`);
    },
  },

  whatsapp: {
    async getStatus() {
      return await whatsappApi.get('/whatsapp/status')
    },

    async getStatusDetalhado() {
      return await whatsappApi.get('/whatsapp/status-detalhado')
    },

    async iniciar() {
      return await whatsappApi.post('/whatsapp/restart')
    },

    async obterQRCode() {
      return await whatsappApi.get('/whatsapp/qrcode')
    },

    async logout() {
      return await whatsappApi.post('/whatsapp/disconnect')
    },

    async desconectar() {
      return await whatsappApi.post('/whatsapp/disconnect')
    },

    async enviarMensagem(telefone, mensagem) {
      return await whatsappApi.post('/whatsapp/send-message', {
        phone: telefone,
        message: mensagem
      }, { timeout: 90000 })
    },

    async verificarNumero(telefone) {
      return await whatsappApi.post('/whatsapp/check-number', {
        phone: telefone
      })
    },

    async formatarTelefone(telefone) {
      let numero = telefone.replace(/\D/g, '');
      if (!numero.startsWith('55')) numero = '55' + numero;
      return { success: true, data: { telefone: numero } };
    },

    async buscarMensagensRecebidas(telefone) {
      return await whatsappApi.get(`/whatsapp/mensagens-recebidas/${telefone}`)
    },

    async marcarMensagemLida(telefone) {
      return await whatsappApi.post(`/whatsapp/mensagens-recebidas/${telefone}/marcar-lida`)
    },

    async limparSessao() {
      return await whatsappApi.post('/whatsapp/limpar-sessao')
    },
  },

  configuracoes: {
    async getWhatsapp() {
      return await api.get('/api/configuracoes/whatsapp')
    },

    async salvarWhatsapp(provedor, modo, ipRemoto) {
      return await api.post('/api/configuracoes/whatsapp', {
        provedor,
        modo,
        ip_remoto: ipRemoto
      })
    },

    // Status da API oficial direto no servidor whatsapp-oficial, mesmo
    // quando ela não é o provedor ativo
    async getStatusWhatsappOficial() {
      return await api.get('/api/configuracoes/whatsapp/oficial/status')
    },
  },
}
// ─── Métodos genéricos para chamadas diretas ──────────────────────────────────
apiService.get  = (url, config) => api.get(url, config);
apiService.post = (url, data, config) => api.post(url, data, config);
apiService.put  = (url, data, config) => api.put(url, data, config);
apiService.delete = (url, config) => api.delete(url, config);


export default apiService